/**
 * Hospitals — super-admin master directory. Used by the ambulance service
 * partner form (to attach hospitals) and by the customer app (to suggest
 * hospitals as ambulance drop locations).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import {
  Buildings, Plus, X, Check, Trash, PencilSimple,
  MagnifyingGlass, ShieldCheck, Airplane, FirstAid, DownloadSimple, Upload,
  User, Key, SirenIcon,
} from "@phosphor-icons/react";

const KERALA_DISTRICTS = [
  "Thiruvananthapuram","Kollam","Pathanamthitta","Alappuzha","Kottayam",
  "Idukki","Ernakulam","Thrissur","Palakkad","Malappuram","Kozhikode","Wayanad",
  "Kannur","Kasaragod",
];

const empty = {
  name: "", speciality: "",
  is_super_speciality: false,
  has_trauma_care: false,
  has_road_accident_care: false,
  has_air_ambulance: false,
  state: "Kerala", district: "", lsgd: "",
  address: "", pincode: "", phone: "", email: "",
  lat: "", lng: "", is_active: true,
};

export default function Hospitals() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);   // null | {} (new) | {full row}
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/hospitals", { params: { q } });
      setRows(r.data.hospitals || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/hospitals/${h.id}`);
      toast.success("Hospital deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/admin/hospitals/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hospitals-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a .csv file");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/hospitals/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { created, updated, skipped, errors } = r.data;
      toast.success(`Import done — ${created} created, ${updated} updated, ${skipped} skipped`);
      if (errors && errors.length) {
        console.warn("CSV row errors:", errors);
        toast.error(`${errors.length} row(s) had issues — see console`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="admin-hospitals">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Master Directory</div>
          <h1 className="font-heading text-2xl mt-1 flex items-center gap-2">
            <Buildings size={22} weight="duotone" className="text-[#DC2626]"/>
            Hospital Directory
          </h1>
          <div className="text-[12px] text-slate-500 mt-0.5">
            {rows.length} {rows.length === 1 ? "hospital" : "hospitals"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={downloadTemplate}
                   data-testid="hosp-template"
                   className="h-10 px-3 rounded-md bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold inline-flex items-center gap-1.5">
            <DownloadSimple size={13} weight="bold"/> Template
          </button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden"
                  data-testid="hosp-upload-input"
                  onChange={(e) => uploadCsv(e.target.files?.[0])}/>
          <button onClick={() => fileRef.current?.click()} disabled={importing}
                   data-testid="hosp-upload"
                   className="h-10 px-3 rounded-md bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-800 text-sm font-semibold inline-flex items-center gap-1.5">
            <Upload size={13} weight="bold"/> {importing ? "Importing…" : "Upload CSV"}
          </button>
          <button onClick={() => setEditing({ ...empty })}
                   data-testid="hosp-new"
                   className="h-10 px-4 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-semibold inline-flex items-center gap-1.5">
            <Plus size={13} weight="bold"/> Add hospital
          </button>
        </div>
      </header>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 h-11">
        <MagnifyingGlass size={14} weight="duotone" className="text-slate-400"/>
        <input value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="hosp-search"
                placeholder="Search name / speciality / district…"
                className="flex-1 text-sm outline-none"/>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Hospital</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Location</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Care</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400 text-sm">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500 text-sm">
                No hospitals yet — add your first one.
              </td></tr>
            ) : rows.map(h => (
              <tr key={h.id} data-testid={`hosp-row-${h.id}`}
                   className="hover:bg-slate-50/50">
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{h.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {h.speciality || "General"}
                    {h.is_super_speciality && (
                      <span className="ml-2 text-[9px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Super speciality</span>
                    )}
                    {!h.is_active && (
                      <span className="ml-2 text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Inactive</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {[h.lsgd, h.district, h.state].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {h.has_trauma_care && <Chip Icon={FirstAid} label="Trauma" color="#DC2626"/>}
                    {h.has_road_accident_care && <Chip Icon={ShieldCheck} label="Road-accident" color="#EA580C"/>}
                    {h.has_air_ambulance && <Chip Icon={Airplane} label="Air-ambulance" color="#2563EB"/>}
                    {!h.has_trauma_care && !h.has_road_accident_care && !h.has_air_ambulance && (
                      <span className="text-[11px] text-slate-400">General</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...h })}
                          data-testid={`hosp-edit-${h.id}`}
                          className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-900">
                    <PencilSimple size={13} weight="duotone"/>
                  </button>
                  <button onClick={() => remove(h)}
                          data-testid={`hosp-del-${h.id}`}
                          className="p-1.5 rounded hover:bg-rose-100 text-slate-500 hover:text-rose-700">
                    <Trash size={13} weight="duotone"/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <HospitalForm value={editing} onClose={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }}/>
      )}
    </div>
  );
}

function Chip({ Icon, label, color }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
          style={{ background: color + "15", color }}>
      <Icon size={10} weight="fill"/> {label}
    </span>
  );
}

function HospitalForm({ value, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ ...value }));
  const [busy, setBusy] = useState(false);
  const isEdit = !!value.id;
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const save = async () => {
    if ((f.name || "").trim().length < 2) { toast.error("Hospital name required"); return; }
    const payload = {
      ...f,
      lat: f.lat === "" || f.lat === null ? null : Number(f.lat),
      lng: f.lng === "" || f.lng === null ? null : Number(f.lng),
    };
    setBusy(true);
    try {
      if (isEdit) await api.put(`/admin/hospitals/${value.id}`, payload);
      else await api.post("/admin/hospitals", payload);
      toast.success(isEdit ? "Hospital updated" : "Hospital created");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto"
          onClick={onClose} data-testid="hosp-form">
      <div className="bg-white rounded-xl w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg">{isEdit ? "Edit hospital" : "Add hospital"}</div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={14}/></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <L wide label="Hospital name *">
            <input value={f.name} onChange={(e) => set("name", e.target.value)}
                   data-testid="hf-name" className={inputCls}/>
          </L>
          <L label="Speciality">
            <input value={f.speciality || ""} onChange={(e) => set("speciality", e.target.value)}
                   placeholder="e.g. Cardiology, Multi-speciality"
                   data-testid="hf-spec" className={inputCls}/>
          </L>
          <L label="Phone">
            <input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)}
                   data-testid="hf-phone" className={inputCls}/>
          </L>

          <ToggleRow k="is_super_speciality" f={f} set={set} label="Super speciality" testid="hf-super"/>
          <ToggleRow k="has_trauma_care" f={f} set={set} label="Trauma care" testid="hf-trauma"/>
          <ToggleRow k="has_road_accident_care" f={f} set={set} label="Road accident care" testid="hf-road"/>
          <ToggleRow k="has_air_ambulance" f={f} set={set} label="Air ambulance slot" testid="hf-air"/>

          <L label="State">
            <input value={f.state || ""} onChange={(e) => set("state", e.target.value)}
                   data-testid="hf-state" className={inputCls}/>
          </L>
          <L label="District">
            <select value={f.district || ""} onChange={(e) => set("district", e.target.value)}
                    data-testid="hf-district" className={inputCls}>
              <option value="">—</option>
              {KERALA_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </L>
          <L label="LSGD">
            <input value={f.lsgd || ""} onChange={(e) => set("lsgd", e.target.value)}
                   data-testid="hf-lsgd" className={inputCls}/>
          </L>
          <L label="Pincode">
            <input value={f.pincode || ""} onChange={(e) => set("pincode", e.target.value)}
                   data-testid="hf-pincode" className={inputCls}/>
          </L>
          <L wide label="Address">
            <textarea rows={2} value={f.address || ""} onChange={(e) => set("address", e.target.value)}
                      data-testid="hf-addr" className={inputCls + " py-2"}/>
          </L>
          <L label="Latitude">
            <input type="number" step="0.00001" value={f.lat ?? ""}
                    onChange={(e) => set("lat", e.target.value)}
                    data-testid="hf-lat" className={inputCls + " font-mono"}/>
          </L>
          <L label="Longitude">
            <input type="number" step="0.00001" value={f.lng ?? ""}
                    onChange={(e) => set("lng", e.target.value)}
                    data-testid="hf-lng" className={inputCls + " font-mono"}/>
          </L>
          <ToggleRow k="is_active" f={f} set={set} label="Active"
                     testid="hf-active"/>
        </div>

        {isEdit && (
          <div className="mt-6 border-t border-slate-200 pt-4">
            <DispatcherManager hospitalId={value.id}/>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose}
                   className="h-10 px-4 border border-slate-300 text-slate-700 rounded-md text-sm">
            Cancel
          </button>
          <button onClick={save} disabled={busy}
                   data-testid="hf-save"
                   className="h-10 px-4 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-semibold inline-flex items-center gap-1.5">
            {busy ? "Saving…" : (<><Check size={13} weight="bold"/> {isEdit ? "Update" : "Create"}</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-10 px-3 border border-slate-300 rounded-md text-sm outline-none focus:border-[#DC2626]";
function L({ label, wide, children }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
function ToggleRow({ k, f, set, label, testid }) {
  const on = !!f[k];
  return (
    <button type="button" onClick={() => set(k, !on)}
             data-testid={testid}
             className={`h-11 rounded-md border text-sm font-semibold inline-flex items-center justify-between px-3 transition-colors ${
               on ? "bg-[#DC2626] text-white border-[#DC2626]"
                  : "bg-white text-slate-800 border-slate-300"
             }`}>
      <span>{label}</span>
      <span className={`text-[10px] font-mono ${on ? "text-white/80" : "text-slate-400"}`}>
        {on ? "YES" : "NO"}
      </span>
    </button>
  );
}


/* ── Dispatcher (hospital-user) manager ── */
const HOSP_PERM_OPTIONS = [
  { key: "view",         label: "View queue",         hint: "See the incident list & dashboard" },
  { key: "triage",       label: "Triage & intake",    hint: "Create incidents · AI triage · voice intake" },
  { key: "dispatch",     label: "Dispatch",           hint: "Change incident status (ack / dispatched / arrived)" },
  { key: "close",        label: "Close incidents",    hint: "Resolve & close cases" },
  { key: "voice",        label: "Voice intake",       hint: "Whisper transcription" },
  { key: "manage_users", label: "Manage dispatchers", hint: "Reserved for hospital-admin role" },
];

function PermissionPicker({ value = [], onChange, testidPrefix = "hosp-disp-perm" }) {
  const set = new Set(value || []);
  const toggle = (k) => {
    const next = new Set(set);
    if (next.has(k)) next.delete(k); else next.add(k);
    onChange(Array.from(next));
  };
  return (
    <div className="sm:col-span-2" data-testid={`${testidPrefix}-picker`}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500 mb-1.5">
        Permissions ({set.size || "none — will default to view only"})
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {HOSP_PERM_OPTIONS.map(p => (
          <label key={p.key}
                  data-testid={`${testidPrefix}-${p.key}`}
                  className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition
                    ${set.has(p.key) ? "border-[#DC2626] bg-[#FEF2F2]" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
            <input type="checkbox" checked={set.has(p.key)}
                    onChange={() => toggle(p.key)}
                    className="mt-0.5 accent-[#DC2626]"/>
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-slate-800">{p.label}</div>
              <div className="text-[10px] text-slate-500 leading-snug">{p.hint}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function DispatcherManager({ hospitalId }) {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    role: "dispatcher", permissions: ["view", "triage", "dispatch"],
  });
  const [editingId, setEditingId] = useState(null);
  const [editPerms, setEditPerms] = useState([]);

  const load = async () => {
    try {
      const r = await api.get(`/admin/hospitals/${hospitalId}/users`);
      setUsers(r.data.users || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hospitalId]);

  const createUser = async () => {
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error("Name, email and a 6+ char password are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/hospitals/${hospitalId}/users`, form);
      toast.success("Dispatcher created — credentials emailed");
      setForm({ name: "", email: "", password: "", phone: "", role: "dispatcher", permissions: ["view", "triage", "dispatch"] });
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/hospitals/${hospitalId}/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const savePermissions = async (u) => {
    try {
      await api.put(`/admin/hospitals/${hospitalId}/users/${u.id}`, { permissions: editPerms });
      toast.success("Permissions updated");
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove dispatcher ${u.name}?`)) return;
    try {
      await api.delete(`/admin/hospitals/${hospitalId}/users/${u.id}`);
      toast.success("Dispatcher removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPw = async (u) => {
    const p = window.prompt(`New password for ${u.name} (min 6 chars):`);
    if (!p || p.length < 6) return;
    try {
      await api.put(`/admin/hospitals/${hospitalId}/users/${u.id}`, { password: p });
      toast.success("Password reset");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="hosp-dispatchers">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">
            EmDispatch access
          </div>
          <div className="font-heading text-base flex items-center gap-1.5">
            <SirenIcon size={16} weight="duotone" className="text-[#DC2626]"/>
            Dispatcher accounts
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Login URL: <code className="bg-slate-100 px-1 rounded">/emdispatch/login</code>
          </div>
        </div>
        <button type="button" onClick={() => setShowForm(true)}
                data-testid="hosp-disp-add"
                className="h-9 px-3 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-semibold inline-flex items-center gap-1.5">
          <Plus size={11} weight="bold"/> Add dispatcher
        </button>
      </div>

      {showForm && (
        <div className="mb-3 p-3 border border-slate-200 rounded-md bg-slate-50 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Full name"
                  data-testid="hosp-disp-name"
                  className={inputCls}/>
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email" type="email"
                  data-testid="hosp-disp-email"
                  className={inputCls}/>
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Password (6+ chars)" type="text"
                  data-testid="hosp-disp-password"
                  className={inputCls + " font-mono"}/>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone"
                  data-testid="hosp-disp-phone"
                  className={inputCls}/>
          <PermissionPicker
            value={form.permissions}
            onChange={(perms) => setForm({ ...form, permissions: perms })}
          />
          <div className="sm:col-span-2 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)}
                     className="h-9 px-3 border border-slate-300 rounded-md text-xs">Cancel</button>
            <button type="button" onClick={createUser} disabled={busy}
                     data-testid="hosp-disp-save"
                     className="h-9 px-3 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-xs font-semibold">
              {busy ? "Creating…" : "Create dispatcher"}
            </button>
          </div>
        </div>
      )}

      {users.length === 0 ? (
        <div className="text-[12px] text-slate-500 italic text-center py-4 border border-dashed border-slate-200 rounded">
          No dispatcher accounts yet. Add one to enable /emdispatch access for this hospital.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {users.map(u => (
            <li key={u.id}
                data-testid={`hosp-disp-row-${u.id}`}
                className="border border-slate-200 rounded-md hover:bg-slate-50">
              <div className="flex items-center gap-3 p-2.5">
                <div className={`w-8 h-8 rounded-full grid place-items-center ${u.is_active ? "bg-[#FEE2E2] text-[#DC2626]" : "bg-slate-200 text-slate-400"}`}>
                  <User size={14} weight="fill"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{u.email} · {u.role || "dispatcher"}</div>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {(u.permissions && u.permissions.length ? u.permissions : ["view"]).map(p => (
                      <span key={p}
                             data-testid={`hosp-disp-${u.id}-perm-${p}`}
                             className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                <button type="button" onClick={() => {
                    setEditingId(editingId === u.id ? null : u.id);
                    setEditPerms(u.permissions || ["view"]);
                  }}
                         title="Edit permissions"
                         data-testid={`hosp-disp-edit-perms-${u.id}`}
                         className={`text-[10px] font-bold px-2 py-1 rounded ${editingId === u.id ? "bg-[#DC2626] text-white" : "bg-slate-200 text-slate-600"}`}>
                  PERMS
                </button>
                <button type="button" onClick={() => resetPw(u)}
                         title="Reset password"
                         className="p-1.5 rounded hover:bg-slate-200 text-slate-500">
                  <Key size={12}/>
                </button>
                <button type="button" onClick={() => toggleActive(u)}
                         title={u.is_active ? "Disable" : "Enable"}
                         className={`text-[10px] font-bold px-2 py-1 rounded ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                  {u.is_active ? "ACTIVE" : "OFF"}
                </button>
                <button type="button" onClick={() => removeUser(u)}
                         className="p-1.5 rounded hover:bg-rose-100 text-slate-500 hover:text-rose-700">
                  <Trash size={12}/>
                </button>
              </div>
              {editingId === u.id && (
                <div className="p-3 border-t border-slate-200 bg-slate-50 space-y-2">
                  <PermissionPicker
                    value={editPerms}
                    onChange={setEditPerms}
                    testidPrefix={`hosp-disp-${u.id}-perm`}
                  />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditingId(null)}
                             className="h-8 px-3 border border-slate-300 rounded text-xs">Cancel</button>
                    <button type="button" onClick={() => savePermissions(u)}
                             data-testid={`hosp-disp-save-perms-${u.id}`}
                             className="h-8 px-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded text-xs font-semibold">
                      Save permissions
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
